import { useCallback, useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import type { ShortcutTokenItem } from '@mlc/shared';
import { formatRelativeDate } from '@mlc/shared';
import { createShortcutToken, listShortcutTokens, revokeShortcutToken } from './api';
import { LIEN_RACCOURCI_IOS } from './raccourciIOS';
import { Banner, Button, TextInput, buttonClasses } from './ui';

/**
 * Le raccourci « Ajouter à Mijoté » — écran d'installation (US-72, vague G).
 *
 * ## Ce que cet écran doit résoudre
 *
 * Quand on tombe sur une recette dans une vidéo, on est en train de scroller.
 * Le sas (US-71) a réglé la moitié du problème — valider plus tard, en une tape
 * ; celui-ci règle l'autre — **capturer sans quitter l'application où l'on
 * est**. Reel → Partager → Mijoté, et Instagram est toujours à l'écran.
 *
 * ## Pourquoi un mode d'emploi et non un fichier à installer
 *
 * Le plan de vague prévoyait « le fichier à installer ». Il n'y en a pas, et
 * c'est un constat, pas un renoncement : **Apple signe les raccourcis
 * distribués**. Un fichier fabriqué par une instance auto-hébergée n'est pas
 * signé ; l'installer suppose d'aller autoriser les raccourcis non fiables dans
 * les réglages d'iOS, et un fichier mal formé se solde par un « impossible
 * d'importer » que personne ne sait diagnostiquer. Fabriquer à l'aveugle un
 * fichier qu'on ne peut pas essayer aurait produit exactement ce que le §1
 * proscrit : **une fonction qui échoue à l'usage**.
 *
 * Ce que l'application peut donner honnêtement, elle le donne : l'adresse de
 * son instance, un jeton, et sept actions à recopier. Deux minutes, une fois
 * pour toutes.
 *
 * ## L'action qui a l'air superflue et ne l'est pas
 *
 * La cinquième — « Encoder en base64 » — est celle qu'on retirerait en croyant
 * simplifier. Les Raccourcis **convertissent en texte rendu** toute page coulée
 * dans un champ JSON : la légende arrive, le balisage disparaît, et le serveur
 * cherche alors le compte et l'image dans un texte qui ne les porte plus. Rien
 * ne le signale — l'entrée est créée, lisible, simplement amputée. Le mode
 * d'emploi le dit à l'endroit où la tentation est.
 *
 * ## Le jeton s'affiche une fois
 *
 * C'est la seule réponse de toute l'API qui contient un jeton en clair, et elle
 * ne se rejoue pas. Un écran qui le réafficherait le mettrait dans un cache,
 * une capture d'écran ou un journal — même raisonnement que la clé d'API
 * d'US-69, qui ne se relit pas davantage. Perdu, il se remplace : on en crée un
 * autre, et l'ancien se révoque.
 */

/** L'adresse de cette instance, telle que le téléphone devra l'écrire. */
function adresseDeLInstance(): string {
  return typeof window === 'undefined' ? '' : window.location.origin;
}

/**
 * Une valeur à recopier dans les Raccourcis.
 *
 * Le bouton **ne change pas de libellé** pendant l'action (§4 « Composition ») :
 * la confirmation vit dans une ligne d'état à part, qui se lit sans faire
 * perdre de vue ce qu'on avait demandé.
 */
function ValeurACopier({ label, valeur }: { label: string; valeur: string }): React.JSX.Element {
  const [copie, setCopie] = useState(false);

  return (
    <div className="flex flex-col gap-1">
      <p className="text-label">{label}</p>
      <div className="flex flex-wrap items-center gap-2">
        <span className="min-w-0 flex-1 break-all rounded-control border border-border bg-surface-sunken px-3 py-2 text-data">
          {valeur}
        </span>
        <Button
          variant="secondary"
          onClick={() => {
            // Absent en contexte non sécurisé et dans certains navigateurs : la
            // valeur reste lisible et sélectionnable à l'écran, qui est le
            // chemin de secours — pas un message d'erreur.
            void navigator.clipboard?.writeText(valeur).then(
              () => { setCopie(true); },
              () => { setCopie(false); },
            );
          }}
        >
          Copier
        </Button>
      </div>
      {copie && <p role="status" className="text-xs text-foreground-secondary">Copié.</p>}
    </div>
  );
}

/** Le mode d'emploi, avec les valeurs de cette instance déjà dedans. */
function ModeDEmploi({ jeton }: { jeton: string | null }): React.JSX.Element {
  const adresse = adresseDeLInstance();

  return (
    <div className="flex flex-col gap-4">
      {/*
        **Le lien d'installation d'abord, le mode d'emploi derrière** (US-106).

        Ce fichier a longtemps commencé par « sept actions, une fois pour
        toutes » : la seule voie possible, puisque Mijoté ne peut pas
        distribuer de fichier de raccourci — Apple les signe, et une instance
        auto-hébergée n'a rien pour signer. Un iPhone, lui, le peut : le lien
        iCloud qu'il produit en partageant le raccourci est signé et s'installe
        d'une tape.

        Ce lien existe désormais (`raccourciIOS.ts`). Le mode d'emploi ne
        disparaît pas pour autant — un lien iCloud peut être révoqué par celui
        qui l'a créé, et il faut alors pouvoir refaire le raccourci à la main —
        mais il devient ce qu'il aurait toujours dû être : le repli, replié.
      */}
      <div className="flex flex-col gap-2 rounded-card border border-border-interactive p-3">
        <h4 className="text-heading-3">Installer le raccourci</h4>
        <p className="text-sm text-foreground-secondary">
          Une tape depuis un iPhone, et il apparaît dans l’app <strong>Raccourcis</strong>. Il ne porte aucun
          jeton : c’est la seule chose qu’il vous restera à y coller, et elle est ci-dessous.
        </p>
        <div>
          <a
            href={LIEN_RACCOURCI_IOS}
            target="_blank"
            rel="noreferrer noopener"
            className={buttonClasses('primary', 'compact')}
          >
            Ouvrir le raccourci
          </a>
        </div>
        <p className="text-xs text-foreground-muted">
          Le lien s’ouvre dans l’app Raccourcis. Depuis un ordinateur, il n’affichera qu’un aperçu.
        </p>
      </div>

      <h4 className="text-heading-3">Le refaire à la main, si le lien ne fonctionne plus</h4>
      {/*
        **Chaque ligne de ce mode d'emploi se fait recopier telle quelle.** La
        première installation réelle l'a démontré trois fois : une notation qui
        veut dire « remplacez ceci » est tapée telle quelle, et un « + » qui
        veut dire « collez bout à bout » finit dans le champ. Le texte ne
        contient donc plus aucun signe qui ne soit à saisir : ce qui se tape est
        entre repères typographiques, et ce qui se choisit dans une liste est
        nommé exactement comme iOS le nomme.
      */}
      <ol className="flex list-decimal flex-col gap-3 pl-6 text-sm">
        <li>
          Dans l’application <strong>Raccourcis</strong> de l’iPhone, créez un raccourci et nommez-le
          « Ajouter à Mijoté ».
        </li>
        <li>
          Ajoutez <strong>Obtenir un composant d’URL</strong>. Deux réglages, et les deux valeurs par défaut
          sont fausses :
          <ul className="mt-1 flex list-disc flex-col gap-1 pl-6">
            <li>
              le composant doit être <strong>Chemin d’accès</strong>, pas <em>Schéma</em> — ce dernier est
              proposé d’office et ne renvoie que <span className="text-data">https</span> ;
            </li>
            <li>
              le champ après « depuis » doit porter la variable <strong>Entrée du raccourci</strong>. S’il
              affiche <em>URL</em> en gris pâle, c’est un emplacement vide : l’action lira du vide et rendra
              du vide, <strong>sans rien signaler</strong>.
            </li>
          </ul>
        </li>
        <li>
          Ajoutez <strong>Texte</strong> et composez l’adresse de la page lisible en trois morceaux collés
          bout à bout, <strong>sans espace et sans signe entre eux</strong> :
          <ol className="mt-1 flex list-[lower-alpha] flex-col gap-1 pl-6">
            <li>tapez <span className="text-data">https://www.instagram.com</span> ;</li>
            <li>
              insérez aussitôt la variable <em>Composant d’URL</em>, depuis la barre au-dessus du clavier ;
            </li>
            <li>
              tapez <span className="text-data">/embed/captioned/</span> juste derrière — <strong>avec ses
              deux barres obliques</strong> : iOS retire celle qui termine le chemin, et sans la vôtre les
              deux morceaux se collent en un nom de domaine qui n’existe pas.
            </li>
          </ol>
          Vous devez lire <strong>une seule ligne continue</strong>, avec le jeton bleu au milieu, du type
          <span className="text-data"> https://www.instagram.com/reel/Db0N9rkMMav/embed/captioned/</span>.
        </li>
        <li>
          Ajoutez <strong>Obtenir le contenu de l’URL</strong> sur ce texte. C’est <strong>le téléphone</strong>
          qui lit la page — le serveur ne va jamais la chercher lui-même.
        </li>
        <li>
          Ajoutez <strong>Encoder</strong> sur ce contenu, réglée sur <strong>base64</strong> (et non
          <em> Décoder</em>). <strong>Cette action n’est pas un détail :</strong> une page passée telle quelle
          dans un champ de texte est <strong>convertie en texte affiché</strong> par iOS — la légende arrive,
          mais le balisage est retiré en chemin, et Mijoté ne peut plus y trouver ni le compte ni l’image.
          L’encodage travaille sur les octets, et la page arrive entière.
        </li>
        <li>
          Ajoutez un second <strong>Obtenir le contenu de l’URL</strong>. Dans son champ d’adresse, collez
          celle du bas de cet écran <strong>en texte</strong> — pas une variable : c’est Mijoté qu’on appelle,
          pas la page qu’on vient de lire. Puis méthode <strong>POST</strong>, un en-tête, et un corps
          <strong> JSON</strong> à deux champs. <strong>Les deux noms de clé se tapent à la main :</strong>
          <ul className="mt-1 flex list-disc flex-col gap-1 pl-6">
            <li>
              <span className="text-data">html_base64</span> — valeur : le <em>Texte codé</em> produit à
              l’étape 5. Vérifiez bien la variable choisie : le <em>contenu de l’URL</em> est proposé d’office
              et lui ressemble, mais c’est la page <strong>non encodée</strong>, celle qu’iOS dépouille.
            </li>
            <li>
              <span className="text-data">source_url</span> — valeur : l’<strong>entrée du raccourci</strong>,
              l’adresse partagée en entier. Pas le <em>composant d’URL</em> de l’étape 2, qui n’en est qu’un
              morceau.
            </li>
          </ul>
        </li>
        <li>
          Dans les réglages du raccourci, activez <strong>Afficher dans la feuille de partage</strong> et
          <strong>décochez tout sauf URL</strong>. Ce n’est pas cosmétique : en acceptant tout, iOS peut
          remettre autre chose qu’une adresse, et l’étape 2 n’aura rien à lire.
        </li>
      </ol>

      {/* Hors de la liste : ce n'est pas une huitième action, c'est la façon de
          l'essayer — et le titre annonce sept actions. */}
      <p className="text-sm text-foreground-secondary">
        Pour l’essayer, passez par la <strong>feuille de partage d’un Reel</strong> — pas par le bouton de
        lecture de l’app Raccourcis, qui n’envoie aucune entrée : le chemin serait vide, et l’adresse de
        l’étape 3 aussi. Pour voir ce qui a été composé, posez l’action <strong>Afficher dans Coup
        d’œil</strong> juste après l’étape à vérifier, puis retirez-la — elle attend une tape, ce qui annule
        tout l’intérêt. <strong>Vérifiez la variable qu’elle affiche :</strong> posée après l’étape 5, elle
        propose d’office le <em>contenu de l’URL</em> et non le <em>Texte codé</em> — elle montre alors
        l’étape précédente, et laisse croire que l’encodage n’a rien changé.
      </p>

      {/*
        L'adresse est celle **d'où l'on regarde cet écran**. Ouvrir les réglages
        depuis un serveur de développement y mettrait `localhost`, que le
        téléphone ne saura pas joindre — d'où la phrase, plutôt qu'une valeur
        qui aurait l'air juste.
      */}
      <ValeurACopier label="Adresse à appeler (étape 6)" valeur={`${adresse}/api/import/draft`} />
      <p className="text-xs text-foreground-secondary">
        C’est l’adresse par laquelle vous consultez Mijoté en ce moment. Si elle commence par
        <span className="text-data"> localhost</span>, votre téléphone ne saura pas la joindre : ouvrez cet
        écran depuis l’adresse publique de l’instance avant de recopier.
      </p>
      <ValeurACopier label="Nom de l’en-tête (étape 6)" valeur="x-mijote-jeton" />

      {jeton !== null && (
        <>
          <ValeurACopier label="Valeur de l’en-tête — votre jeton" valeur={jeton} />
          <Banner tone="warning">
            Ce jeton ne se réaffichera pas. Recopiez-le maintenant dans le raccourci ; si vous le perdez,
            créez-en un autre et révoquez celui-ci.
          </Banner>
        </>
      )}

      <p className="text-sm text-foreground-secondary">
        Une fois installé : sur un Reel, <strong>Partager</strong> puis <strong>Ajouter à Mijoté</strong>. La
        recette arrive dans votre sas, et vous restez sur Instagram. Si la page ne donne rien — compte privé,
        publication effacée —, l’entrée est créée quand même, avec son lien : une capture d’écran et l’import
        depuis une photo prennent le relais.
      </p>

      {/*
        **La corvée se paie une fois pour la maison, pas une fois par personne.**

        Mijoté ne peut pas distribuer de fichier de raccourci : Apple signe les
        raccourcis partagés, et une instance auto-hébergée n'a pas de quoi
        signer. Mais un iPhone, si — quand c'est *l'utilisateur* qui partage
        depuis l'app Raccourcis, le lien iCloud produit est signé et s'installe
        d'une tape.

        La seule chose que ce lien ne peut pas porter est le jeton, qui nomme
        une personne : le suivant colle le sien, et c'est tout ce qui lui reste
        à faire.
      */}
      <p className="text-sm text-foreground-secondary">
        C’est ainsi qu’a été fabriqué le lien du haut : dans l’app <strong>Raccourcis</strong>, « Partager » sur
        un raccourci produit un lien iCloud signé. Si vous en refaites un, remplacez la valeur dans
        <span className="text-data"> client/src/raccourciIOS.ts</span> — le commentaire y dit tout.
      </p>
    </div>
  );
}

export function ShortcutSetup(): React.JSX.Element {
  const [raccourcis, setRaccourcis] = useState<ShortcutTokenItem[] | null>(null);
  const [label, setLabel] = useState('');
  const [jeton, setJeton] = useState<string | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [occupe, setOccupe] = useState(false);
  // Instant figé au montage : un rendu doit être pur, et « vu il y a 3 jours »
  // n'a pas besoin d'être recalculé à la seconde (même règle que DeviceMode).
  const [maintenant] = useState(() => Date.now());

  const echec = useCallback((e: unknown) => {
    setErreur(e instanceof Error ? e.message : String(e));
  }, []);

  const recharger = useCallback(() => {
    listShortcutTokens()
      .then(({ shortcuts }) => { setRaccourcis(shortcuts); })
      .catch(() => {
        // Un échec de lecture ne transforme pas les réglages en page d'erreur :
        // la liste s'affiche vide, et le formulaire de création reste offert.
        setRaccourcis([]);
      });
  }, []);

  useEffect(recharger, [recharger]);

  function creer(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    setErreur(null);
    setNotice(null);
    setOccupe(true);
    createShortcutToken(label)
      .then((reponse) => {
        setLabel('');
        setJeton(reponse.token);
        recharger();
      })
      .catch(echec)
      .finally(() => { setOccupe(false); });
  }

  function revoquer(raccourci: ShortcutTokenItem): void {
    setErreur(null);
    setNotice(null);
    revokeShortcutToken(raccourci.id)
      .then(() => {
        setNotice(`${raccourci.label} ne peut plus rien envoyer à votre sas.`);
        recharger();
      })
      .catch(echec);
  }

  return (
    <div className="flex flex-col gap-4">
      {erreur !== null && <Banner tone="danger">{erreur}</Banner>}
      {notice !== null && <Banner tone="success">{notice}</Banner>}

      <p className="text-sm text-foreground-secondary">
        Un raccourci envoie ce que vous partagez depuis votre téléphone — une recette vue dans une vidéo, un
        message — vers <strong>votre sas</strong>. Il n’ouvre rien d’autre : le jeton ne permet pas de lire une
        recette, seulement d’en mettre une de côté. Chacun installe le sien.
      </p>

      {raccourcis !== null && raccourcis.length > 0 && (
        <ul className="flex list-none flex-col gap-2 p-0">
          {raccourcis.map((raccourci) => (
            <li
              key={raccourci.id}
              className="flex flex-wrap items-center gap-3 rounded-card border border-border bg-surface-raised px-3 py-2"
            >
              <span>{raccourci.label}</span>
              <span className="text-sm text-foreground-secondary">
                {raccourci.last_used_at === null
                  ? 'jamais utilisé'
                  : `utilisé ${formatRelativeDate(raccourci.last_used_at, maintenant)}`}
              </span>
              <Button variant="ghost" className="ml-auto" onClick={() => { revoquer(raccourci); }}>
                Révoquer
              </Button>
            </li>
          ))}
        </ul>
      )}

      <form onSubmit={creer} className="flex flex-wrap items-end gap-2">
        <label className="flex flex-1 flex-col gap-1 text-sm">
          Nom de l’appareil
          <TextInput
            type="text"
            autoComplete="off"
            placeholder="iPhone d’Arthur"
            value={label}
            onChange={(e) => { setLabel(e.target.value); }}
          />
        </label>
        <Button type="submit" variant="secondary" disabled={occupe || label.trim() === ''} aria-busy={occupe}>
          Créer un jeton
        </Button>
      </form>

      <ModeDEmploi jeton={jeton} />
    </div>
  );
}
